import type { TypedClient } from '@platform/openapi';
import { useState } from 'react';
import { defaultDepotOf, onHandAt } from './inventory.ts';
import {
  MAX_ADJUSTMENT_QUANTITY,
  RECEIPT_REASON,
  reviewAdjustment,
  type AdjustmentPlan,
} from './stock-adjustment.ts';
import type { StockRow } from './StockMatrix.tsx';
import { useStockMatrix } from './useStockMatrix.ts';

const withinLimit = (quantity: number): number => Math.min(Math.max(quantity, 0), MAX_ADJUSTMENT_QUANTITY);

const receiptText = (quantity: number): string => (quantity === 0 ? '' : String(quantity));

export const useQuickAddPlan = (api: TypedClient, rows: readonly StockRow[]) => {
  const stock = useStockMatrix(
    api,
    rows.map((row) => row.variantId),
  );
  const [quantities, setQuantities] = useState<readonly number[]>(() => rows.map(() => 0));
  const [activeLine, setActiveLine] = useState(0);
  const [chosenDepot, setChosenDepot] = useState('');
  const locationId = chosenDepot === '' ? defaultDepotOf(stock.locations) : chosenDepot;
  const plan: AdjustmentPlan = { reason: RECEIPT_REASON, locationId, unitPrice: '', note: '' };
  const lines = rows.map((row, position) => ({
    row,
    onHand: onHandAt(stock.levels, row.variantId, locationId),
    quantity: quantities[position] ?? 0,
  }));
  const review = reviewAdjustment(
    plan,
    lines.map((line) => ({
      variantId: line.row.variantId,
      onHand: line.onHand,
      text: receiptText(line.quantity),
    })),
  );

  const setQuantity = (position: number, quantity: number) => {
    setQuantities((current) =>
      current.map((value, index) => (index === position ? withinLimit(quantity) : value)),
    );
  };

  const addToActiveLine = (amount: number) => {
    setQuantity(activeLine, (quantities[activeLine] ?? 0) + amount);
  };

  return {
    stock,
    lines,
    review,
    locationId,
    activeLine,
    setActiveLine,
    setQuantity,
    addToActiveLine,
    chooseDepot: setChosenDepot,
  };
};
